import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';

export interface TimeSeriesBucket {
  key: string;
  label: string;
  fullDate: string;
  valid: number;
  spam: number;
  total: number;
}

export interface AnalyticsSeriesPoint {
  timestamp: string;
  submissions?: number;
  valid?: number;
  spam?: number;
}

export interface AnalyticsResponse {
  submissions?: number;
  valid?: number;
  spam?: number;
  p50_latency_ms?: number;
  p95_latency_ms?: number;
  p99_latency_ms?: number;
  storage_bytes?: number;
  files_count?: number;
  series?: AnalyticsSeriesPoint[];
}

export function useAnalyticsData() {
  const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
  const { currentProject, projects, tables } = useAuth();

  const effectiveProjectId = routeProjectId || currentProject?.id;
  const project = useMemo(() => {
    if (currentProject?.id === effectiveProjectId) return currentProject;
    return projects.find((p) => p.id === effectiveProjectId) || currentProject;
  }, [currentProject, projects, effectiveProjectId]);

  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse | null>(null);

  // Real latency percentiles
  const [latencyP50, setLatencyP50] = useState(0);
  const [latencyP95, setLatencyP95] = useState(0);
  const [latencyP99, setLatencyP99] = useState(0);
  const [totalSubmissions, setTotalSubmissions] = useState(0);
  const [validSubmissions, setValidSubmissions] = useState(0);
  const [spamSubmissions, setSpamSubmissions] = useState(0);

  const inFlightKeyRef = useRef<string | null>(null);
  const lastFetchTimeRef = useRef(0);

  // Failsafe safety timeout: ensure loading state never hangs for more than 2s
  useEffect(() => {
    const safetyTimer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(safetyTimer);
  }, []);

  const fetchAnalyticsAndLogs = useCallback(
    async (force = false) => {
      if (!effectiveProjectId) {
        setIsLoading(false);
        return;
      }
      const fetchKey = `${effectiveProjectId}:${timeRange}`;
      if (inFlightKeyRef.current === fetchKey && !force) {
        return;
      }
      inFlightKeyRef.current = fetchKey;
      lastFetchTimeRef.current = Date.now();
      setIsLoading(true);

      try {
        const res = await apiClient<AnalyticsResponse>(
          `/projects/${effectiveProjectId}/analytics?range=${timeRange}`
        );
        setAnalyticsData(res);
        if (typeof res?.submissions === 'number') {
          setTotalSubmissions(res.submissions);
          setValidSubmissions(res.valid ?? Math.max(0, res.submissions - (res.spam ?? 0)));
          setSpamSubmissions(res.spam ?? 0);
          if (res.submissions > 0) {
            setLatencyP50(res.p50_latency_ms || 0);
            setLatencyP95(res.p95_latency_ms || 0);
            setLatencyP99(res.p99_latency_ms || 0);
          } else {
            setLatencyP50(0);
            setLatencyP95(0);
            setLatencyP99(0);
          }
        }
      } catch (err) {
        console.warn('Analytics endpoint returned error:', err);
      } finally {
        if (inFlightKeyRef.current === fetchKey) {
          inFlightKeyRef.current = null;
        }
        setIsLoading(false);
      }
    },
    [effectiveProjectId, timeRange]
  );

  useEffect(() => {
    fetchAnalyticsAndLogs();
  }, [fetchAnalyticsAndLogs]);

  // Auto-refresh when window gains focus (debounced to at most once per 30s)
  useEffect(() => {
    const handleFocus = () => {
      if (
        document.visibilityState === 'visible' &&
        Date.now() - lastFetchTimeRef.current > 30000
      ) {
        fetchAnalyticsAndLogs();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [fetchAnalyticsAndLogs]);

  // Periodic poll every 30 seconds when tab is active
  useEffect(() => {
    if (!effectiveProjectId) return;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAnalyticsAndLogs();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [effectiveProjectId, fetchAnalyticsAndLogs]);

  // Time series buckets for the SVG area chart
  const timeSeriesBuckets: TimeSeriesBucket[] = useMemo(() => {
    const is24h = timeRange === '24h';
    const is7d = timeRange === '7d';
    const count = is24h ? 24 : is7d ? 7 : 30;
    const now = new Date();

    const rawMap = new Map<string, AnalyticsSeriesPoint>();
    if (analyticsData?.series && Array.isArray(analyticsData.series)) {
      for (const pt of analyticsData.series) {
        if (!pt.timestamp) continue;
        const d = new Date(pt.timestamp);
        if (isNaN(d.getTime())) continue;
        const key = is24h
          ? `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}T${String(d.getUTCHours()).padStart(2, '0')}`
          : `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
        rawMap.set(key, pt);
      }
    }

    const buckets: TimeSeriesBucket[] = [];

    if (is24h) {
      const baseHour = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0));
      for (let i = count - 1; i >= 0; i--) {
        const bucketDate = new Date(baseHour.getTime() - i * 3600000);
        const key = `${bucketDate.getUTCFullYear()}-${String(bucketDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bucketDate.getUTCDate()).padStart(2, '0')}T${String(bucketDate.getUTCHours()).padStart(2, '0')}`;
        const matched = rawMap.get(key);

        const total = matched ? (matched.submissions ?? (matched.valid ?? 0) + (matched.spam ?? 0)) : 0;
        const spam = matched ? (matched.spam ?? 0) : 0;
        const valid = matched ? (matched.valid ?? Math.max(0, total - spam)) : 0;

        buckets.push({
          key,
          label: `${String(bucketDate.getHours()).padStart(2, '0')}:00`,
          fullDate: bucketDate.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
          total,
          valid,
          spam,
        });
      }
    } else {
      const baseDay = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
      for (let i = count - 1; i >= 0; i--) {
        const bucketDate = new Date(baseDay.getTime() - i * 86400000);
        const key = `${bucketDate.getUTCFullYear()}-${String(bucketDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bucketDate.getUTCDate()).padStart(2, '0')}`;
        const matched = rawMap.get(key);

        const total = matched ? (matched.submissions ?? (matched.valid ?? 0) + (matched.spam ?? 0)) : 0;
        const spam = matched ? (matched.spam ?? 0) : 0;
        const valid = matched ? (matched.valid ?? Math.max(0, total - spam)) : 0;

        buckets.push({
          key,
          label: bucketDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' }),
          fullDate: bucketDate.toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          total,
          valid,
          spam,
        });
      }
    }

    return buckets;
  }, [timeRange, analyticsData?.series]);

  // Active Ingress endpoints statistics
  const dynamicCount = tables.filter((t) => t.schema_mode !== 'strict').length;
  const strictCount = tables.filter((t) => t.schema_mode === 'strict').length;

  // Spam quarantine calculations with precision threshold
  const spamRate = totalSubmissions > 0 ? (spamSubmissions / totalSubmissions) * 100 : 0;
  const cleanRate = totalSubmissions > 0 ? (validSubmissions / totalSubmissions) * 100 : 100;

  const displaySpamRate =
    totalSubmissions === 0 || spamSubmissions === 0
      ? '0.00%'
      : spamSubmissions === totalSubmissions
      ? '100.00%'
      : spamRate < 0.01
      ? '< 0.01%'
      : spamRate > 99.99
      ? '> 99.99%'
      : `${spamRate.toFixed(2)}%`;

  const displayCleanRate =
    totalSubmissions === 0 || validSubmissions === totalSubmissions
      ? '100.00%'
      : validSubmissions === 0
      ? '0.00%'
      : cleanRate > 99.99
      ? '> 99.99%'
      : cleanRate < 0.01
      ? '< 0.01%'
      : `${cleanRate.toFixed(2)}%`;

  return {
    project,
    tables,
    timeRange,
    setTimeRange,
    isLoading,
    analyticsData,
    latencyP50,
    latencyP95,
    latencyP99,
    totalSubmissions,
    validSubmissions,
    spamSubmissions,
    timeSeriesBuckets,
    dynamicCount,
    strictCount,
    displaySpamRate,
    displayCleanRate,
    fetchAnalyticsAndLogs,
  };
}
